import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AssistantRedirect, AssistantSectionPage } from '@pages/assistant-detail';
import { HomePage } from '@pages/home';
import { HelpPage } from '@pages/legacy';
import { ApiPage } from '@pages/api-access';
import { OperatorsPage } from '@pages/operators';
import { OperatorLinkPage } from '@pages/operator-link';
import { CreateAssistantPage } from '@pages/assistant-create';
import { AssistantSettingsPage } from '@pages/assistant-settings';
import { ModelPickerPage } from '@pages/model-picker';
import { TestChatPage } from '@pages/test-chat';
import { KnowledgePage, SiteConnectPage, SitePage } from '@pages/knowledge';
import { BillingV2Page } from '@pages/billing-v2';
import { ChannelConnectPage, ChannelRoute, ChannelsPage } from '@pages/channels';
import { DialogsPage } from '@pages/dialogs';
import { BroadcastEditPage, BroadcastsPage } from '@pages/broadcasts';
import { ForgotPasswordPage } from '@pages/forgot-password';
import { GettingStartedPage } from '@pages/getting-started';
import { HelpSectionPage } from '@pages/help-section';
import { LegalIndexPage } from '@pages/legal-index';
import { LegalPrivacyPage } from '@pages/legal-privacy';
import { LegalRequisitesPage } from '@pages/legal-requisites';
import { LegalTermsPage } from '@pages/legal-terms';
import { LoginPage } from '@pages/login';
import { NotFoundPage } from '@pages/not-found';
import { PricingPage } from '@pages/pricing';
import { RegisterPage } from '@pages/register';
import { ResetPasswordPage } from '@pages/reset-password';
import { AccountPage } from '@pages/account';
import { VerifyEmailPage } from '@pages/verify-email';
import { AppShell } from '@widgets/app-shell';
import { LegalLayout } from '@widgets/legal-layout';
import { AuthGuard } from './AuthGuard';
import { GuestOnly } from './GuestOnly';

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Публичные auth-страницы */}
        <Route
          path="/login"
          element={
            <GuestOnly>
              <LoginPage />
            </GuestOnly>
          }
        />
        <Route
          path="/register"
          element={
            <GuestOnly>
              <RegisterPage />
            </GuestOnly>
          }
        />
        <Route
          path="/verify-email"
          element={
            <GuestOnly>
              <VerifyEmailPage />
            </GuestOnly>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestOnly>
              <ForgotPasswordPage />
            </GuestOnly>
          }
        />
        <Route
          path="/reset-password"
          element={
            <GuestOnly>
              <ResetPasswordPage />
            </GuestOnly>
          }
        />

        {/* Серия O: переписка по ссылке из MAX-бота оператора (без входа, по подписанной ссылке) */}
        <Route path="/op/:token" element={<OperatorLinkPage />} />

        {/* Публичные правовые страницы и тарифы (без авторизации) */}
        <Route element={<LegalLayout />}>
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/legal" element={<LegalIndexPage />} />
          <Route path="/legal/terms" element={<LegalTermsPage />} />
          <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
          <Route path="/legal/requisites" element={<LegalRequisitesPage />} />
        </Route>

        {/* «Новый ассистент» (экран 2) — отдельная страница без левого меню */}
        <Route
          path="/assistants/new"
          element={
            <AuthGuard>
              <CreateAssistantPage />
            </AuthGuard>
          }
        />
        <Route
          path="/assistants/new/model"
          element={
            <AuthGuard>
              <div className="v2-app">
                <div className="v2-main">
                  <ModelPickerPage mode="create" view="recommended" />
                </div>
              </div>
            </AuthGuard>
          }
        />
        <Route
          path="/assistants/new/model/all"
          element={
            <AuthGuard>
              <div className="v2-app">
                <div className="v2-main">
                  <ModelPickerPage mode="create" view="all" />
                </div>
              </div>
            </AuthGuard>
          }
        />

        {/* Приватный кабинет v2 (V2-4): левое меню + страница */}
        <Route
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="/assistants" element={<Navigate to="/" replace />} />
          <Route path="/assistants/:id" element={<AssistantRedirect />} />
          <Route path="/assistants/:id/channels" element={<ChannelsPage />} />
          <Route path="/assistants/:id/dialogs" element={<DialogsPage />} />
          <Route path="/assistants/:id/broadcasts" element={<BroadcastsPage />} />
          <Route path="/assistants/:id/broadcasts/:bid" element={<BroadcastEditPage />} />
          <Route path="/assistants/:id/channels/new" element={<ChannelConnectPage />} />
          <Route path="/assistants/:id/channels/:channelId" element={<ChannelRoute />} />
          <Route path="/assistants/:id/settings" element={<AssistantSettingsPage />} />
          <Route
            path="/assistants/:id/settings/model"
            element={<ModelPickerPage mode="settings" view="recommended" />}
          />
          <Route
            path="/assistants/:id/settings/model/all"
            element={<ModelPickerPage mode="settings" view="all" />}
          />
          <Route path="/assistants/:id/test-chat" element={<TestChatPage />} />
          <Route path="/assistants/:id/knowledge" element={<KnowledgePage />} />
          <Route path="/assistants/:id/knowledge/site" element={<SiteConnectPage />} />
          <Route path="/assistants/:id/knowledge/site/:sourceId" element={<SitePage />} />
          <Route path="/assistants/:id/:section/*" element={<AssistantSectionPage />} />
          <Route path="/billing" element={<BillingV2Page />} />
          <Route path="/api-access" element={<ApiPage />} />
          <Route path="/operators" element={<OperatorsPage />} />
          <Route
            path="/getting-started"
            element={
              <HelpPage>
                <GettingStartedPage />
              </HelpPage>
            }
          />
          <Route
            path="/getting-started/:slug"
            element={
              <HelpPage>
                <HelpSectionPage />
              </HelpPage>
            }
          />
          <Route path="/settings" element={<AccountPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
